/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useState } from 'react';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import './MeterSavingsTableTheme.scss';
import { IUtilityMeterSavingsDetailTableProps } from './IMeterSavingsDetailTableProps';
import {
  formatCurrency,
  getAbsoluteValue,
  getMeterName,
  getUtilityImage
} from '../Utils/Utils';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  KPI_VALUE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD,
  UtilityDetailMeterField,
  UtilityMeterField
} from '../Utils/Constants';
import CommonProgressBar from '../../shared/CommonProgressBar/CommonProgressBar';
import { getPercentageColor } from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import {
  renderActualConsumCell,
  renderTableColumns
} from './MeterSavingsDetailTableUtility';
import { ApiContext } from '../../../context/ApiContext';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';

const UtilityMeterSavingsDetailTable = (
  props: IUtilityMeterSavingsDetailTableProps
) => {
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(
    UtilityMeterField.totalActualConsumption
  );
  const {
    meterData,
    utilityCostCurrency,
    utilityMeasurementUnit,
    columnDetails,
    utilityType
  } = props;

  const apiContext = useContext(ApiContext);

  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    const currencyconverter = formatCurrency(value, utilityCostCurrency!);

    if (field === UtilityDetailMeterField.totalActualConsumption) {
      return renderActualConsumCell(
        cellData,
        utilityMeasurementUnit,
        props.originalUnit,
        utilityType
      );
    }
    if (field === UtilityDetailMeterField.totalConsumptionSavings) {
      if (
        apiContext.showChangeWhenData &&
        (Number(rowData.previousPeriodConsumption) === 0 ||
          Math.abs(rowData.percentageConsumptionSaving) >
            PERCENT_CHANGE_THRESHOLD)
      ) {
        return <span className="score-category-meterdata-meter">--</span>;
      } else {
        return (
          <span className="score-category-meterdata-meter">
            <span
              className="savings-badge"
              style={{
                color: getPercentageColor(value)
              }}
            >
              {getAbsoluteValue(
                Number(
                  getConvertedValue(
                    utilityType ? utilityType : 'Electricity',
                    props.originalUnit ? props.originalUnit : 'kilowatt hour',
                    value,
                    rowData.changeUnit
                  )?.Value
                )
              ).toFixed(KPI_VALUE_DECIMAL_PRECISION)}
              {' ' + props.originalUnit && utilityType
                ? rowData.changeUnit +
                  '(' +
                  Math.abs(rowData.percentageConsumptionSaving).toFixed(
                    KPI_PERCENTAGE_DECIMAL_PRECISION
                  ) +
                  '%)'
                : utilityMeasurementUnit +
                  '(' +
                  Math.abs(rowData.percentageConsumptionSaving).toFixed(
                    KPI_PERCENTAGE_DECIMAL_PRECISION
                  ) +
                  '%)'}
            </span>
          </span>
        );
      }
    }
    if (field === UtilityDetailMeterField.totalCostSavings) {
      if (value === null) {
        return <span className="score-category-meterdata-meter">--</span>;
      } else {
        return (
          <div className="score-category-meterdata-meter">
            <div
              className="savings-badge"
              style={{
                display: 'flex',
                color: getPercentageColor(value)
              }}
            >
              <div>{currencyconverter}</div>
              <div>
                {'(' +
                  Math.abs(rowData.percentageCostSavings).toFixed(
                    KPI_PERCENTAGE_DECIMAL_PRECISION
                  ) +
                  '%)'}
              </div>
            </div>
          </div>
        );
      }
    }
    if (field === UtilityDetailMeterField.Meter) {
      return (
        <span className="score-category-meterdata-meter ">
          <span className="score-category-Badge">
            {getUtilityImage(rowData.meterType)}
          </span>
          <span className="score-category-percent">{getMeterName(value)}</span>
        </span>
      );
    }

    if (field === UtilityDetailMeterField.previousPeriodCost) {
      return (
        <span className="score-category-meterdata-meter">
          <span className="score-category-percent">{currencyconverter}</span>
        </span>
      );
    }
    if (field === UtilityDetailMeterField.totalActualCost) {
      return (
        <span className="score-category-meterdata">
          <span className="score-category-percent">
            <div className="measurementunit">{currencyconverter}</div>
          </span>
          <span className="score-category-progress">
            <CommonProgressBar
              actual={rowData.totalActualCost}
              previousPeriod={rowData.previousPeriodCost}
              savings={rowData.totalCostSavings}
              savingsPercentage={rowData.totalCostSavingsPercentage}
              progessBarRelativeWidth={rowData.progessBarRelativeWidthCost}
            />
          </span>
        </span>
      );
    }
    if (field === UtilityDetailMeterField.previousPeriodConsumption) {
      return (
        <span className="score-category-meterdata-meter">
          <div className="consumptionvalue">
            {getConvertedValue(
              utilityType ? utilityType : 'Electricity',
              props.originalUnit ? props.originalUnit : 'kilowatt hour',
              value,
              rowData.baselineUnit
            )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION)}
          </div>
          {props.originalUnit && utilityType
            ? rowData.baselineUnit
            : utilityMeasurementUnit}
        </span>
      );
    }
    return <></>;
  };

  return (
    <DataTable
      className="Savingtable"
      data={meterData}
      scrollable={true}
      scrollHeight="20rem"
      onSort={({ sortField, sortOrder }) => {
        setFieldSortOrder(sortOrder as number);
        setSortFieldName(sortField);
      }}
      sortField={sortFieldName}
      sortOrder={fieldSortOrder}
    >
      {renderTableColumns(columnDetails, cellDataRenderer)}
    </DataTable>
  );
};
export default UtilityMeterSavingsDetailTable;
